import { useCallback } from 'react';
import { useAuth } from 'src/services/auth/useAuth';
import useDataActions from 'src/services/data/actions';
import { useDataDispatch, useDataState } from 'src/services/data/context';
import useDataQueries from 'src/services/data/queries';
import useAuthActions from './actions';
import { useAuthDispatch, useAuthState } from './context';
import { normalizeScopes } from '../../utils/utils';

export const useTenantManagement = () => {
  const { clientList, loadingStates } = useDataState();
  const { sessionToken, userId, isStaff } = useAuthState();

  const dispatch = useAuthDispatch();
  const dataDispatch = useDataDispatch();

  const { loginSucceeded } = useAuthActions(dispatch);
  const { authenticatedRequest, username } = useAuth();

  const {
    getClientList,
    setClientActive,
    getUserScopeList,
    getTenantScopeList,
  } = useDataQueries(authenticatedRequest);

  const { setMultiData, setLoading } = useDataActions(dataDispatch);

  // Get the list of all the accessible tenant for the user
  const fetchInitialClientList = useCallback(async () => {
    setLoading('fetchInitialClientList', true);

    const clientListPromises = getClientList().then((clientList) => {
      return clientList;
    });

    Promise.all([
      clientListPromises.catch((error) => console.error(error)),
    ]).then((values) => {
      setMultiData({
        clientList: values[0],
      });
    });

    setLoading('fetchInitialClientList', false);
  }, [getClientList, setLoading, setMultiData]);

  // Refresh the access token of the user with the one of his selected tenant
  const onClientActive = useCallback(
    async (id, name) => {
      setLoading('onClientActive', true);
      try {
        const setClientActiveResponse = await setClientActive(id);
        let userScopes;
        if (isStaff) {
          userScopes = await getTenantScopeList(id);
        } else {
          userScopes = await getUserScopeList(userId);
        }

        if (setClientActiveResponse.status === 200) {
          loginSucceeded({
            username: username,
            clientName: name,
            scopes: normalizeScopes(userScopes),
            userId,
            clientId: id,
            isStaff: isStaff,
            sessionToken,
          });
        }
      } catch (error) {
        setLoading('onClientActive', false);
        throw error;
      }

      setLoading('onClientActive', false);
    },
    [
      setLoading,
      setClientActive,
      getUserScopeList,
      getTenantScopeList,
      isStaff,
      userId,
      loginSucceeded,
      username,
      sessionToken,
    ]
  );

  return {
    // State
    clientList,
    loadingStates,
    // Actions
    fetchInitialClientList,
    onClientActive,
  };
};
